import { ERROR_CODE_MAPPING } from '@/config/error-codes';
import i18n from '@/lib/i18n';
import type { ApiError } from '@/types/api';

/**
 * Строит ключ перевода с учетом payload
 * Например: errors.accounts.already_exists:type:phone
 */
const buildTranslationKey = (
  baseKey: string,
  payload?: Record<string, unknown>,
): string => {
  if (!payload || Object.keys(payload).length === 0) {
    return baseKey;
  }

  const payloadParts: string[] = [];
  for (const [key, value] of Object.entries(payload)) {
    if (value !== null && value !== undefined) {
      payloadParts.push(`${key}:${value}`);
    }
  }

  if (payloadParts.length > 0) {
    const compositeKey = `${baseKey}:${payloadParts.join(':')}`;
    const translated = i18n.t(compositeKey);
    if (translated !== compositeKey) {
      return compositeKey;
    }
  }

  return baseKey;
};

export const getErrorMessage = (
  errorCode: string,
  fallbackMessage?: string,
  payload?: Record<string, unknown>,
): string => {
  const baseTranslationKey = ERROR_CODE_MAPPING[errorCode];

  if (baseTranslationKey) {
    const translationKey = buildTranslationKey(baseTranslationKey, payload);
    const translatedMessage = i18n.t(translationKey);

    if (translatedMessage !== translationKey) {
      return translatedMessage;
    }
  }

  return fallbackMessage || i18n.t('errors.system.unknown_error', 'Произошла неизвестная ошибка');
};

function isApiError(error: unknown): error is ApiError {
  return typeof error === 'object' && error !== null && 'message' in error;
}

function getApiErrorCode(error: unknown): string | undefined {
  if (!isApiError(error)) {
    return undefined;
  }

  if (error.standardizedCode) {
    return error.standardizedCode;
  }

  const detailsCode = error.details?.code;
  return typeof detailsCode === 'string' ? detailsCode : undefined;
}

export function getApiErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    const message = String(error.message);

    if (message && !/^HTTP Error: \d+$/.test(message)) {
      return message;
    }

    const code = getApiErrorCode(error);
    if (code) {
      return getErrorMessage(code, undefined, error.details?.payload);
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return i18n.t('errors.system.unknown_error', 'Произошла неизвестная ошибка');
}
